import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Input } from "@chakra-ui/input";
import {
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  useColorMode,
} from "@chakra-ui/react";
import { at } from "lodash";
import React from "react";
import NumberFormat, { NumberFormatProps } from "react-number-format";
import useFieldFast from "../../hooks/useFieldFast";
import useInProgressDisable from "../../hooks/useInProgressDisable";
import { css } from "@emotion/react";

export type TextInputFieldProps = {
  label: string;
  name: string;
  type?: string;
  errorText?: string;
  helperText?: string;
  children?: any;
  disabled?: any;
  isReadOnly?: any;
  formatNumber?: boolean;
  placeholder?: string | number;
  formatOptions?: NumberFormatProps;
};

function TextInputField(props: TextInputFieldProps) {
  const {
    label,
    name,
    type,
    helperText,
    children,
    disabled,
    isReadOnly,
    formatNumber,
    placeholder,
    formatOptions,
  } = props;

  const [field, meta, helpers] = useFieldFast(props);
  const { onChange, ...fieldProps } = field;
  const { colorMode } = useColorMode();

  function renderHelperText() {
    const [touched, error] = at(meta, "touched", "error");
    if (touched && error) {
      return error;
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (meta.touched) return;

    let click = e.target as HTMLInputElement;
    click.select();
  };

  return (
    <FormControl
      id={name}
      isInvalid={meta.touched && !!meta.error}
      isDisabled={useInProgressDisable(name) || disabled}
    >
      <FormLabel>{label}</FormLabel>
      <InputGroup>
        {type === "number" && formatNumber ? (
          <Input
            as={NumberFormat}
            name={name}
            value={field.value}
            isNumericString={false}
            isReadOnly={isReadOnly}
            thousandSeparator={true}
            onValueChange={(vals: any) => helpers.setValue(vals.value)}
            {...fieldProps}
            onClick={handleClick}
            placeholder={placeholder}
            {...formatOptions}
          />
        ) : (
          <Input
            type={type}
            {...field}
            onClick={handleClick}
            placeholder={placeholder}
            isReadOnly={isReadOnly}
            css={
              type === "date" &&
              colorMode === "dark" &&
              css`
                ::-webkit-calendar-picker-indicator {
                  filter: invert(1);
                }
              `
            }
            data-testid={`${name}-input`}
          />
        )}

        {children}
      </InputGroup>
      {meta.touched && meta.error ? (
        <FormErrorMessage>{renderHelperText()}</FormErrorMessage>
      ) : (
        helperText && <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

export default TextInputField;
